import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { eur } from "@/lib/format";

// Cambiar fechas, horas o vehículo de una reserva. Con el vehículo entregado
// (estado "active") solo se puede mover la devolución.
export default function ScheduleEditor({ reservation: r, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [freeIds, setFreeIds] = useState(null);
  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const rentalPaid = r.rental_payment_status === "paid";
  const active = r.status === "active";
  const closed = !["pending", "active"].includes(r.status);

  const start = () => {
    setForm({
      vehicle_id: r.vehicle_id || "", pickup_date: r.pickup_date, pickup_time: r.pickup_time,
      return_date: r.return_date, return_time: r.return_time,
      total_amount: r.total_amount ?? 0,
    });
    api.get("/fleet/vehicles").then(({ data }) => setVehicles(data.vehicles || [])).catch(() => {});
    setOpen(true);
  };

  useEffect(() => {
    if (!form || !form.pickup_date || !form.return_date || form.return_date < form.pickup_date) return;
    api.get("/fleet/availability", { params: { start: form.pickup_date, end: form.return_date, exclude: r.id } })
      .then(({ data }) => setFreeIds(new Set(data.vehicles.filter((v) => v.free).map((v) => v.id))))
      .catch(() => setFreeIds(null));
  }, [form?.pickup_date, form?.return_date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form || !form.vehicle_id || !form.pickup_date || !form.return_date || form.return_date < form.pickup_date) {
      setQuote(null);
      return;
    }
    api.get("/fleet/quote", { params: { vehicle_id: form.vehicle_id, start: form.pickup_date, end: form.return_date } })
      .then(({ data }) => setQuote(data))
      .catch(() => setQuote(null));
  }, [form?.vehicle_id, form?.pickup_date, form?.return_date]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/reservations/${r.id}/schedule`, { ...form, total_amount: Number(form.total_amount) });
      toast.success("Reserva actualizada");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  if (closed) return null;
  return (
    <>
      <Button variant="ghost" size="sm" onClick={start} data-testid="schedule-edit-btn">
        <CalendarClock className="h-4 w-4 mr-1" /> Cambiar fechas o vehículo
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fechas y vehículo</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4" data-testid="schedule-form">
              {active && (
                <p className="text-sm text-[#5C5C5C]">El vehículo ya está entregado: solo se puede cambiar la devolución.</p>
              )}
              <div>
                <Label>Vehículo</Label>
                <select value={form.vehicle_id} onChange={set("vehicle_id")} disabled={active}
                  className="mt-1 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm" data-testid="schedule-vehicle">
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id} disabled={v.id !== r.vehicle_id && (!v.active || (freeIds && !freeIds.has(v.id)))}>
                      {v.name} · {v.plate}{freeIds && !freeIds.has(v.id) && v.id !== r.vehicle_id ? " — ocupado" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Recogida</Label><Input type="date" value={form.pickup_date} onChange={set("pickup_date")} disabled={active} /></div>
                <div><Label>Hora</Label><Input type="time" value={form.pickup_time} onChange={set("pickup_time")} disabled={active} /></div>
                <div><Label>Devolución</Label><Input type="date" value={form.return_date} onChange={set("return_date")} data-testid="schedule-return-date" /></div>
                <div><Label>Hora</Label><Input type="time" value={form.return_time} onChange={set("return_time")} /></div>
              </div>
              <div>
                <Label>Importe total del alquiler (€)</Label>
                <Input type="number" step="0.01" value={form.total_amount} onChange={set("total_amount")} disabled={rentalPaid} data-testid="schedule-total" />
                {rentalPaid ? (
                  <p className="text-xs text-[#5C5C5C] mt-1">El alquiler ya está cobrado: el importe no se cambia desde aquí.</p>
                ) : quote && (
                  <p className="text-xs mt-1" data-testid="schedule-quote">
                    {quote.ok ? (
                      <>
                        Precio según tarifas: <strong>{eur(quote.base_total)}</strong> ({quote.nights} {quote.nights === 1 ? "noche" : "noches"}){" "}
                        <button type="button" className="underline text-marca ml-1"
                          onClick={() => setForm((f) => ({ ...f, total_amount: quote.base_total }))} data-testid="schedule-quote-use">
                          Usar este importe
                        </button>
                      </>
                    ) : (
                      <span className="text-acento">{quote.error}</span>
                    )}
                  </p>
                )}
                <p className="text-xs text-[#8A8A8A] mt-1">Al cambiar de vehículo se aplican su fianza y sus km si aún no se han cobrado.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={save} disabled={saving} className="bg-marca hover:bg-marca-hover text-white" data-testid="schedule-save">
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
